/**
 * @file: ManualLeadForm.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * The manual-lead intake form: a vehicle card (plate keyboard + VIN + brand/model
 * pick + mileage), a customer card, a lead card (target date / origin / assignee /
 * type), and an advanced section (visibility / follow-up window / remarks), then
 * the create CTA.
 */

import { Typography } from '@euler/app/components/typography';
import { useManualLeadForm } from '@euler/app/flows/leads/pages/create-manual-lead/functions/useManualLeadForm';
import { SaveButton } from '@euler/app/flows/leads/pages/create-reservation/components/SaveButton';
import { Card } from '@euler/app/flows/leads/pages/create-reservation/components/common/Card';
import { Eyebrow } from '@euler/app/flows/leads/pages/create-reservation/components/common/Eyebrow';
import { FieldRow } from '@euler/app/flows/leads/pages/create-reservation/components/common/FieldRow';
import { VisibilityPicker } from '@euler/app/flows/leads/pages/detail/components/VisibilityPicker';
import { LD } from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import { DateTimePicker } from '@euler/components/DateTimePicker';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsDesktopLayout } from '@euler/functions/responsiveness';
import {
  LeadOriginLabel,
  LeadTypeLabel,
  LeadVisibilityLabel,
} from '@euler/model/lead/Lead';
import format from 'date-fns/format';
import { CalendarClock, ChevronRight } from 'lucide-react-native';
import { ScrollView, TextInput, TouchableOpacity, View } from 'react-native';

export const ManualLeadForm = () => {
  const { rpx } = useResponsiveContext();
  const isDesktop = useIsDesktopLayout();
  const ctl = useManualLeadForm();
  const { form } = ctl;

  // `alignSelf` is what actually sizes these: FieldRow's value cell is a COLUMN
  // with `alignItems: 'flex-end'`, so a child's width comes from its content and
  // `flex: 1` only grows its height. Left to measure itself, a native TextInput
  // ends up narrower than its own text — clipped on the left while focused,
  // scrolled back to the first character once blurred. Stretch it to the cell
  // instead; `textAlign` keeps the value right-aligned. (`flex: 1` still earns
  // its keep in the mileage row, which lays its input out in a row of its own.)
  const inputStyle = {
    flex: 1,
    alignSelf: 'stretch',
    fontSize: rpx(15),
    color: LD.ink,
    padding: 0,
    textAlign: 'right',
  } as const;

  // Match the picker rows: a placeholder renders regular, a typed value bold.
  const fontFor = (filled: boolean) =>
    filled ? FontFamily.NotoSans.Bold : FontFamily.NotoSans.Regular;

  return (
    <View style={{ flex: 1, backgroundColor: LD.canvas }}>
      <ScrollView
        contentContainerStyle={{ padding: rpx(14), paddingBottom: rpx(36) }}
        keyboardShouldPersistTaps="handled"
      >
        <View
          style={
            isDesktop
              ? { width: '100%', maxWidth: rpx(560), alignSelf: 'center' }
              : undefined
          }
        >
          <Eyebrow first required>
            车辆信息
          </Eyebrow>
          <Card>
            <FieldRow
              label="车牌号"
              required
              first
              labelAccessory={
                <TouchableOpacity
                  onPress={ctl.onOpenPlateKeyboard}
                  hitSlop={{ top: 8, bottom: 8, left: 4, right: 8 }}
                  style={{ marginLeft: rpx(8) }}
                >
                  <Typography.Text size={rpx(12)} color={LD.brand} medium>
                    车牌键盘
                  </Typography.Text>
                </TouchableOpacity>
              }
            >
              <TextInput
                value={form.licensePlateNo.value}
                onChangeText={ctl.onChangeLicensePlate}
                onBlur={ctl.onLookupPlate}
                placeholder="输入车牌号"
                placeholderTextColor={LD.mist}
                autoCapitalize="characters"
                style={[
                  inputStyle,
                  { fontFamily: fontFor(!!form.licensePlateNo.value) },
                ]}
              />
            </FieldRow>
            <FieldRow label="车架号">
              <TextInput
                value={form.vin.value}
                onChangeText={form.vin.update}
                placeholder="自动识别或手动输入"
                placeholderTextColor={LD.mist}
                autoCapitalize="characters"
                style={[inputStyle, { fontFamily: fontFor(!!form.vin.value) }]}
              />
            </FieldRow>
            <FieldRow label="车型" onPress={ctl.onPickVehicle}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Typography.Text
                  size={rpx(15)}
                  color={ctl.vehicleLabel ? LD.ink : LD.mist}
                  bold={ctl.vehicleLabel ? true : undefined}
                  numberOfLines={1}
                >
                  {ctl.lookingUp
                    ? '识别中…'
                    : ctl.vehicleLabel || '未识别 · 点击选择'}
                </Typography.Text>
                <ChevronRight
                  size={rpx(16)}
                  color={LD.mist}
                  style={{ marginLeft: rpx(2) }}
                />
              </View>
            </FieldRow>
            <FieldRow label="进店里程">
              <View
                style={{
                  width: '100%',
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <TextInput
                  value={form.mileage.value}
                  onChangeText={t =>
                    form.mileage.update(t.replace(/[^0-9]/g, ''))
                  }
                  keyboardType="number-pad"
                  placeholder="—"
                  placeholderTextColor={LD.mist}
                  style={[
                    inputStyle,
                    { fontFamily: fontFor(!!form.mileage.value) },
                  ]}
                />
                <Typography.Text
                  size={rpx(13)}
                  color={LD.mist}
                  style={{ marginLeft: rpx(6) }}
                >
                  公里
                </Typography.Text>
              </View>
            </FieldRow>
          </Card>

          <Eyebrow required>客户信息</Eyebrow>
          <Card>
            <FieldRow label="客户姓名" required first>
              <TextInput
                value={form.customerName.value}
                onChangeText={form.customerName.update}
                placeholder="填写客户姓名"
                placeholderTextColor={LD.mist}
                style={[
                  inputStyle,
                  { fontFamily: fontFor(!!form.customerName.value) },
                ]}
              />
            </FieldRow>
            <FieldRow label="联系电话" required>
              <TextInput
                value={form.customerPhone.value}
                onChangeText={form.customerPhone.update}
                placeholder="填写联系电话"
                placeholderTextColor={LD.mist}
                keyboardType="phone-pad"
                style={[
                  inputStyle,
                  { fontFamily: fontFor(!!form.customerPhone.value) },
                ]}
              />
            </FieldRow>
          </Card>

          <Eyebrow required>商机信息</Eyebrow>
          <Card>
            <FieldRow
              label="目标服务日期"
              required
              first
              onPress={ctl.showTargetPicker}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <CalendarClock size={rpx(16)} color={LD.brand} />
                <Typography.Text
                  size={rpx(15)}
                  color={LD.ink}
                  bold
                  style={{ marginLeft: rpx(6) }}
                >
                  {format(form.targetDate.value, 'yyyy-MM-dd')}
                </Typography.Text>
              </View>
            </FieldRow>
            <FieldRow label="线索来源" required onPress={ctl.onPickOrigin}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Typography.Text
                  size={rpx(15)}
                  color={form.origin.value ? LD.ink : LD.mist}
                  bold={form.origin.value ? true : undefined}
                >
                  {form.origin.value
                    ? LeadOriginLabel[form.origin.value]
                    : '选择'}
                </Typography.Text>
                <ChevronRight
                  size={rpx(16)}
                  color={LD.mist}
                  style={{ marginLeft: rpx(2) }}
                />
              </View>
            </FieldRow>
            <FieldRow label="商机类型" required onPress={ctl.onPickType}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Typography.Text size={rpx(15)} color={LD.ink} bold>
                  {LeadTypeLabel[form.type.value]}
                </Typography.Text>
                <ChevronRight
                  size={rpx(16)}
                  color={LD.mist}
                  style={{ marginLeft: rpx(2) }}
                />
              </View>
            </FieldRow>
            <FieldRow label="负责人" required onPress={ctl.onPickAgent}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Typography.Text
                  size={rpx(15)}
                  color={form.assignee.value ? LD.ink : LD.mist}
                  bold={form.assignee.value ? true : undefined}
                >
                  {form.assignee.value?.name ?? '选择'}
                </Typography.Text>
                <ChevronRight
                  size={rpx(16)}
                  color={LD.mist}
                  style={{ marginLeft: rpx(2) }}
                />
              </View>
            </FieldRow>
          </Card>

          <Eyebrow>高级选项</Eyebrow>
          <Card>
            <FieldRow label="可见范围" first onPress={ctl.openVisibilityPicker}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Typography.Text size={rpx(15)} color={LD.ink} bold>
                  {LeadVisibilityLabel[form.visibility.value]}
                </Typography.Text>
                <ChevronRight
                  size={rpx(16)}
                  color={LD.mist}
                  style={{ marginLeft: rpx(2) }}
                />
              </View>
            </FieldRow>
            <FieldRow label="跟进开始日期" onPress={ctl.showOutreachPicker}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <CalendarClock size={rpx(16)} color={LD.brand} />
                <Typography.Text
                  size={rpx(15)}
                  color={LD.ink}
                  bold
                  style={{ marginLeft: rpx(6) }}
                >
                  {format(form.outreachStart.value, 'yyyy-MM-dd')}
                </Typography.Text>
              </View>
            </FieldRow>
            <FieldRow label="备注" alignTop>
              <TextInput
                value={form.remarks.value}
                onChangeText={form.remarks.update}
                multiline
                placeholder="填写备注"
                placeholderTextColor={LD.mist}
                textAlignVertical="top"
                style={{
                  width: '100%',
                  minHeight: rpx(60),
                  fontSize: rpx(15),
                  lineHeight: rpx(22),
                  fontFamily: FontFamily.NotoSans.Regular,
                  color: LD.ink,
                  padding: 0,
                  textAlign: 'right',
                }}
              />
            </FieldRow>
          </Card>

          <SaveButton onPress={ctl.submit} />
        </View>
      </ScrollView>

      <DateTimePicker
        isVisible={ctl.targetPickerVisible}
        mode="date"
        date={form.targetDate.value}
        onConfirm={date => {
          form.targetDate.update(date);
          ctl.hideTargetPicker();
        }}
        onCancel={ctl.hideTargetPicker}
        confirmTextIOS="确认"
        cancelTextIOS="关闭"
      />
      <DateTimePicker
        isVisible={ctl.outreachPickerVisible}
        mode="date"
        date={form.outreachStart.value}
        onConfirm={date => {
          ctl.onPickOutreachStart(date);
          ctl.hideOutreachPicker();
        }}
        onCancel={ctl.hideOutreachPicker}
        confirmTextIOS="确认"
        cancelTextIOS="关闭"
      />
      <VisibilityPicker
        visible={ctl.visibilityPickerVisible}
        value={form.visibility.value}
        onClose={ctl.closeVisibilityPicker}
        onSelect={ctl.onSelectVisibility}
      />
    </View>
  );
};
